import z from 'zod'
import {
  validatePassword,
  formatValidationErrors,
} from '@/utils/password-validation'

// ============================================================================
// Base Field Schemas (reusable building blocks)
// ============================================================================

/** Email validation - standard email format */
export const emailSchema = z.email({
  message: 'Please enter a valid email address',
})

/**
 * Password validation using the utility that matches backend rules.
 * Requirements: 8-72 chars, at least one letter, one number, not a common password.
 */
export const passwordSchema = z.string().check((ctx) => {
  const result = validatePassword(ctx.value)
  if (!result.valid) {
    ctx.issues.push({
      code: 'custom',
      message: formatValidationErrors(result),
      input: ctx.value,
      path: [],
    })
  }
})

/** Name field validation - non-empty string */
export const nameSchema = z.string().min(1, {
  message: 'This field is required',
})

/** Terms acceptance - must be true */
export const termsSchema = z.literal(true, {
  message: 'You must accept the terms and conditions',
})

// ============================================================================
// Form Schemas
// ============================================================================

/** Login form - email and password */
export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, { message: 'Password is required' }),
})

export type LoginSchema = z.infer<typeof loginSchema>

/** Signup form - full registration with name, email, password, and terms */
export const signupSchema = z.object({
  firstName: nameSchema.describe('First name'),
  lastName: nameSchema.describe('Last name'),
  email: emailSchema,
  password: passwordSchema,
  terms: termsSchema,
})

export type SignupSchema = z.infer<typeof signupSchema>

/** Forgot password form - email only */
export const forgotPasswordSchema = z.object({
  email: emailSchema,
})

export type ForgotPasswordSchema = z.infer<typeof forgotPasswordSchema>

/** Reset password form - new password with confirmation */
export const resetPasswordSchema = z
  .object({
    password: passwordSchema,
    confirmPassword: z
      .string()
      .min(1, { message: 'Please confirm your password' }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

export type ResetPasswordSchema = z.infer<typeof resetPasswordSchema>
